/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { animated, useTransition } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';

import Cross from '@toucan/client/global/icons/legacy/cross.svg';

import {
    IconButton,
    IconButtonFill,
    IconButtonSize,
} from '@toucan/client/shared/components/icon-button';
import { Link, LinkSize } from '@toucan/client/shared/components/link';

import {
    Accordion,
    AccordionSection,
} from '@toucan/client/blocks/components/accordion';

import { SearchFilterMenuOptionList } from '@toucan/client/features/search/components/option-list';
import { SearchFilterMenuPriceModifierOptions } from '@toucan/client/features/search/components/price-modifier-options';
import { SearchFilterMenuPriceOptions } from '@toucan/client/features/search/components/price-options';
import { PriceFilterOption } from '@toucan/client/features/search/components/price-options/constants';
import { SearchFilterSelectOptions } from '@toucan/client/features/search/components/search-filter/types';
import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/config/types';

import { styles } from './search-filter-group-mobile.styles';

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type SearchFilterGroupMobileProps = {
    /**
     * If the filter is open or not
     */
    isOpen: boolean;

    /**
     * The filters
     */
    filters: Filter[];

    /**
     * The number of results showing
     */
    resultsCount: number;

    /**
     * Handle a filter being opened
     *
     * @param {string} id
     */
    onFilterOpen?: (id: string) => Promise<void> | void;

    /**
     * Handler for when the Cancel button is clicked
     */
    onCancel?: () => void;

    /**
     * Handler for when the Apply button is clicked
     */
    onApply?: (selectedFilters: Filter[]) => void;

    /**
     * Handler for when the Close button is clicked
     */
    onClose?: () => void;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

type SectionTitleChildNodeProps = {
    /**
     * If the accordion section is open
     */
    isOpen: boolean;

    /**
     * The filters options that have been selected by the user
     */
    selectedCount: number;

    /**
     * Handler that will be called when the Clear all button is clicked
     * for the group
     */
    onClearAll: () => void;
};

const StyledSearchFilterGroupMobile = styled(animated.aside).withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

/**
 * A component to handle rendering the extra node for each section
 */
const SectionTitleChildNode = ({
    isOpen,
    onClearAll,
    selectedCount,
}: SectionTitleChildNodeProps) => {
    const { t } = useTranslation();

    if (selectedCount === 0) {
        return null;
    }

    if (!isOpen) {
        return (
            <span className="extra-node-count">
                {t('{{count}} selected', { count: selectedCount })}
            </span>
        );
    }

    return (
        <Link
            size={LinkSize.SMALL}
            className="extra-node-clear"
            onClick={onClearAll}
        >
            {t('Clear all')}
        </Link>
    );
};

export const SearchFilterGroupMobile: FunctionComponent<
    SearchFilterGroupMobileProps
> = ({
    isOpen,
    filters,
    resultsCount,
    onFilterOpen,
    onApply,
    onCancel,
    onClose,
    ...otherProps
}: SearchFilterGroupMobileProps) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [activeAccordionSection, setActiveAccordionSection] = useState<
        string | null
    >(null);

    const [maybeSelectedFilters, setMaybeSelectedFilters] =
        useState<Filter[]>(filters);

    const transitions = useTransition(isOpen, {
        from: {
            transform: 'translateY(5rem)',
            opacity: 0,
        },
        enter: {
            transform: 'translateY(0)',
            opacity: 1,
        },
        leave: {
            transform: 'translateY(5rem)',
            opacity: 0,
        },
        onRest: () => {
            if (!isOpen) {
                setActiveAccordionSection(null);
            }
        },
    });

    /**
     * Checks whether or not a given filter options exists in an array of
     * selected filter options
     *
     * @param {FilterOption[]} selectedFilterOptions The array of selected
     * filter options we are checking against
     * @param {FilterOption} filterOption The filter option that we are checking
     * for in the selectedFilterOptions array
     * @return {boolean} whether or not the given filter option exists in the
     * array of filter options
     */
    // eslint-disable-next-line @mpb-com/mpb/boolean-function-prefix -- Legacy code: do not use as an example.
    function checkFilterOptionIsSelected(
        selectedFilterOptions: FilterOption[],
        filterOption: string | number
    ): boolean {
        return selectedFilterOptions.some((option) => {
            return option.value === filterOption;
        });
    }

    /**
     * Remove a given filter option from an array of given selected filter
     * options
     *
     * @param {FilterOption[]} selectedFilterOptions The array of selected
     * filter options we are removing a filter from
     * @param {FilterOption} filterOption The filter option we are adding to the
     * array of selected filter options
     * @returns {FilterOption[]} The updated array of selectedFilterOptions,
     * with the given option removed
     */
    function removeSelectedFilterOptionFromFilter(
        selectedFilterOptions: FilterOption[],
        filterOption: string | number
    ): FilterOption[] {
        return selectedFilterOptions.filter((option) => {
            return option.value !== filterOption;
        });
    }

    /**
     * Add a given filter option to an array of given selected filter options
     *
     * @param {FilterOption[]} selectedFilterOptions The array of selected
     * filter options we are adding a filter to
     * @param {FilterOption} filterOption The filter option we are adding to the
     * array of selected filter options
     * @returns void
     */
    function addSelectedFilterOptionToFilter(
        selectedFilterOptions: FilterOption[],
        filterOption: FilterOption
    ): void {
        selectedFilterOptions.push(filterOption);
    }

    const handleChoiceChange = (
        filterIndex: number,
        filterOption: FilterOption,
        { overwrite }: SearchFilterSelectOptions = {}
    ) => {
        const updatedMaybeSelectedFilters = [...maybeSelectedFilters];
        const matchingFilter = updatedMaybeSelectedFilters[filterIndex];
        const selectedFilterOptions = matchingFilter.selectedOptions;

        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const filterOptionIsSelected = checkFilterOptionIsSelected(
            selectedFilterOptions,
            filterOption.value
        );

        if (overwrite) {
            matchingFilter.selectedOptions = [filterOption];
            setMaybeSelectedFilters(updatedMaybeSelectedFilters);
            return;
        }

        if (filterOptionIsSelected) {
            matchingFilter.selectedOptions =
                removeSelectedFilterOptionFromFilter(
                    selectedFilterOptions,
                    filterOption.value
                );
        } else {
            addSelectedFilterOptionToFilter(
                selectedFilterOptions,
                filterOption
            );
        }

        setMaybeSelectedFilters(updatedMaybeSelectedFilters);
    };

    const handleVariantChange = (
        filterIndex: number,
        filterVariant: string
    ) => {
        const updatedMaybeSelectedFilters = [...maybeSelectedFilters];
        const matchingFilter = updatedMaybeSelectedFilters[filterIndex];
        matchingFilter.selectedVariant = filterVariant;

        setMaybeSelectedFilters(updatedMaybeSelectedFilters);
    };

    /**
     * Clears any changes to the internal filter state. Effectively resets the
     * menu's filter state to that when its was opened.
     *
     * @returns void
     */
    function clearChangesToFilterGroups(): void {
        setMaybeSelectedFilters(filters);
    }

    /**
     * Removes all selected filter option in a given filter from the internal
     * state.
     *
     * @param {number} filterIndex The index of the filter that we want to update
     * @returns void
     */
    function removeSelectedFilterOptionsInFilterGroup(
        filterIndex: number
    ): void {
        const updatedMaybeSelectedFilters = [...maybeSelectedFilters];
        const matchingFilter = updatedMaybeSelectedFilters[filterIndex];

        updatedMaybeSelectedFilters[filterIndex] = {
            ...matchingFilter,
            selectedOptions: [],
        };

        setMaybeSelectedFilters(updatedMaybeSelectedFilters);
    }

    /**
     * Removes all selected filter options from every filter
     *
     * @returns void
     */
    function removeAllSelectedFilterOptions() {
        const updatedMaybeSelectedFilters = maybeSelectedFilters.map(
            (selectedFilter) => {
                return {
                    ...selectedFilter,
                    selectedVariant: '',
                    selectedOptions: [],
                };
            }
        );

        setMaybeSelectedFilters(updatedMaybeSelectedFilters);
    }

    function renderFilterOptions(filter: Filter, filterIndex: number) {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const { selectedOptions, displayOptionQuantity, selectedVariant } =
            filter;

        if (filter.id === SearchFacet.productPrice) {
            return (
                <SearchFilterMenuPriceOptions
                    filter={filter}
                    selectedOptions={selectedOptions}
                    onSelect={(filter, options) => {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
                        handleChoiceChange(filterIndex, filter, options);
                    }}
                    onClear={() => {
                        removeSelectedFilterOptionsInFilterGroup(filterIndex);
                    }}
                    displayOptionQuantity={displayOptionQuantity}
                    isLoading={isLoading}
                    selectedVariant={selectedVariant as PriceFilterOption}
                    onSelectVariant={(variant) => {
                        handleVariantChange(filterIndex, variant);
                    }}
                    autoFocusPriceGroup={false}
                />
            );
        }

        if (filter.id === SearchFacet.productPriceModifier) {
            return (
                <SearchFilterMenuPriceModifierOptions
                    filter={filter}
                    selectedOptions={selectedOptions}
                    onSelect={(filter, options) => {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
                        handleChoiceChange(filterIndex, filter, options);
                    }}
                    isLoading={isLoading}
                />
            );
        }

        return (
            <SearchFilterMenuOptionList
                filter={filter}
                selectedOptions={selectedOptions}
                onSelect={(filter) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
                    handleChoiceChange(filterIndex, filter);
                }}
                displayOptionQuantity={displayOptionQuantity}
                isLoading={isLoading}
                autoFocus={false}
            />
        );
    }

    /**
     * Returns the accordion sections
     *
     * @returns {AccordionSection[]}
     */
    function getAccordionSections(): AccordionSection[] {
        return maybeSelectedFilters.map((maybeSelectedFilter, filterIndex) => {
            const { label, id } = maybeSelectedFilter;

            return {
                id,
                title: label,
                showTitleChildNode: true,
                titleChildNode: (
                    <SectionTitleChildNode
                        isOpen={activeAccordionSection === id}
                        selectedCount={
                            maybeSelectedFilter.selectedOptions.length
                        }
                        onClearAll={() => {
                            removeSelectedFilterOptionsInFilterGroup(
                                filterIndex
                            );
                        }}
                    />
                ),
                content: renderFilterOptions(maybeSelectedFilter, filterIndex),
            };
        });
    }

    function renderTitle() {
        return t('Filters');
    }

    /**
     * Update the maybeSelectedFilters with incoming filter changes.
     * Taking into account whether an active filter is open, in which case using
     * its locally stored selected items as the source of truth.
     *
     * Scenario: Opening a filter causes new filters to be provided due to async
     * count handling. We need to ignore the selected items incoming from the URL
     * if this happens
     */
    useEffect(() => {
        const preparedFilterStatuses = filters.map((filter) => {
            const localFilterStatus = maybeSelectedFilters.find(
                (maybeSelectedFilter) => maybeSelectedFilter.id === filter.id
            );

            if (!localFilterStatus) {
                return filter;
            }

            return {
                ...filter,
                selectedOptions: localFilterStatus.selectedOptions,
                selectedVariant: localFilterStatus.selectedVariant,
            };
        });

        setMaybeSelectedFilters(preparedFilterStatuses);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [filters]);

    return (
        <Fragment>
            {transitions((props, item) =>
                item ? (
                    <StyledSearchFilterGroupMobile
                        filters={filters}
                        resultsCount={resultsCount}
                        isOpen={isOpen}
                        style={props}
                        {...otherProps}
                    >
                        <div className="filter-content">
                            <div className="filter-header">
                                <Heading
                                    size={HeadingSize.H3}
                                    className="filter-header-title"
                                >
                                    {renderTitle()}
                                </Heading>

                                <IconButton
                                    label={t('Close')}
                                    fill={IconButtonFill.HOLLOW}
                                    border={false}
                                    size={IconButtonSize.MEDIUM}
                                    className="filter-header-close"
                                    onClick={() => {
                                        clearChangesToFilterGroups();

                                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
                                        onClose && onClose();
                                    }}
                                    data-testid="mobile-search-filter__close"
                                >
                                    <Cross />
                                </IconButton>
                                <p className="filter-header-results">
                                    <strong>{resultsCount}</strong>{' '}
                                    {t('results')}
                                </p>
                                {maybeSelectedFilters.length !== 0 && (
                                    <Link
                                        size={LinkSize.SMALL}
                                        className="filter-header-clear"
                                        data-testid="mobile-search-filter__clear-all"
                                        onClick={() => {
                                            removeAllSelectedFilterOptions();
                                        }}
                                    >
                                        {t('Clear all filters')}
                                    </Link>
                                )}
                            </div>

                            <Accordion
                                className="filter-accordion"
                                activeSection={activeAccordionSection}
                                sections={getAccordionSections()}
                                onActiveSectionChange={async (section) => {
                                    setIsLoading(true);
                                    setActiveAccordionSection(section);

                                    if (
                                        typeof section === 'string' &&
                                        onFilterOpen
                                    ) {
                                        await onFilterOpen(section);
                                    }

                                    setIsLoading(false);
                                }}
                            />

                            <div className="filter-buttons">
                                <Button
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    className="filter-button"
                                    onClick={() => {
                                        clearChangesToFilterGroups();
                                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
                                        onCancel && onCancel();
                                    }}
                                    data-testid="mobile-search-filter__cancel"
                                >
                                    {t('Cancel')}
                                </Button>
                                <Button
                                    className="filter-button"
                                    size={ButtonSize.SMALL}
                                    onClick={() => {
                                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
                                        onApply &&
                                            onApply(maybeSelectedFilters);
                                    }}
                                    data-testid="mobile-search-filter__apply"
                                >
                                    {t('Apply')}
                                </Button>
                            </div>
                        </div>
                    </StyledSearchFilterGroupMobile>
                ) : null
            )}
        </Fragment>
    );
};
